<script setup lang="ts">
/**
 * A dialog that asks for one line of text — a name for a new folder, a new
 * title, a link. The field has the focus on open with its value selected, so
 * typing replaces it; Enter submits. `validate` returns an error text (shown
 * under the field once the user typed or tried to submit) and keeps the
 * submit disabled; `required` refuses an empty value. An `error` from outside
 * (the server says the name is taken) shows the same way.
 *
 * Presentational: `v-model:open`, `v-model` for the text, `submit` with the
 * trimmed value and `cancel`. It closes on submit; with `keepOpen` it stays
 * open so the caller can save (`busy`) and then close it or set `error`. For the awaitable form that replaces
 * `window.prompt()`, use `useNxPrompt()` with an `NxConfirmHost` in the shell.
 */
import { computed, nextTick, ref, useId, useTemplateRef, watch } from 'vue';
import { NxButton } from '../Button';
import { NxTextInput } from '../Inputs';
import { NxModal } from '../Modal';

const props = withDefaults(
  defineProps<{
    title: string;
    /** Text above the field. */
    message?: string;
    /** Visible label of the field. */
    label?: string;
    placeholder?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Refuse an empty (whitespace-only) value. */
    required?: boolean;
    /** Error text for an empty required value. */
    requiredMessage?: string;
    /** Returns an error text for an invalid value, else `null`/`undefined`. */
    validate?: (value: string) => string | null | undefined;
    /** Error from outside, e.g. the server refused the name. */
    error?: string;
    /** The submitted value is being saved: buttons disabled, dialog stays open. */
    busy?: boolean;
    /** Stay open after `submit`; the caller closes it (or sets `error`). */
    keepOpen?: boolean;
  }>(),
  {
    message: undefined,
    label: undefined,
    placeholder: undefined,
    confirmLabel: 'Übernehmen',
    cancelLabel: 'Abbrechen',
    required: true,
    requiredMessage: 'Bitte ausfüllen.',
    validate: undefined,
    error: undefined,
    busy: false,
    keepOpen: false,
  }
);

const open = defineModel<boolean>('open', { required: true });
const value = defineModel<string>({ default: '' });

const emit = defineEmits<{
  submit: [value: string];
  cancel: [];
}>();

const fieldId = useId();
const messageId = useId();
const errorId = useId();
const inputRef = useTemplateRef<InstanceType<typeof NxTextInput>>('input');

/** Errors show once the user typed or tried to submit, not on an untouched field. */
const touched = ref(false);
let answered = false;

const localError = computed(() => {
  if (props.required && !value.value.trim()) return props.requiredMessage;
  return props.validate?.(value.value) ?? '';
});
const shownError = computed(() => props.error || (touched.value ? localError.value : ''));
const canSubmit = computed(() => !props.busy && !localError.value);

watch(
  open,
  async (isOpen) => {
    if (!isOpen) return;
    answered = false;
    touched.value = false;
    await nextTick();
    await nextTick();
    const input = inputRef.value?.inputElement as HTMLInputElement | null | undefined;
    input?.focus();
    input?.select();
  },
  { immediate: true }
);

function submit() {
  touched.value = true;
  if (!canSubmit.value) return;
  emit('submit', value.value.trim());
  if (props.keepOpen) return;
  answered = true;
  open.value = false;
}

function cancel() {
  if (props.busy) return;
  answered = true;
  emit('cancel');
  open.value = false;
}

function onShellClose() {
  if (answered) return;
  answered = true;
  emit('cancel');
}
</script>

<template>
  <NxModal
    v-model:open="open"
    class="nx-prompt-dialog"
    :title="props.title"
    :close-on-escape="!props.busy"
    :close-on-click-outside="!props.busy"
    size="default"
    @close="onShellClose"
  >
    <template #body>
      <form class="nx-prompt-dialog__form" novalidate @submit.prevent="submit">
        <p v-if="props.message" :id="messageId" class="nx-prompt-dialog__message">
          {{ props.message }}
        </p>
        <label v-if="props.label" class="nx-prompt-dialog__label" :for="fieldId">
          {{ props.label }}
        </label>
        <NxTextInput
          :id="fieldId"
          ref="input"
          v-model="value"
          :placeholder="props.placeholder"
          :aria-label="props.label ? undefined : props.title"
          :aria-invalid="shownError ? 'true' : undefined"
          :aria-describedby="
            [props.message ? messageId : '', shownError ? errorId : ''].filter(Boolean).join(' ') ||
            undefined
          "
          :disabled="props.busy"
          autocomplete="off"
          data-testid="nx-prompt-input"
          @input="touched = true"
        />
        <p
          v-if="shownError"
          :id="errorId"
          class="nx-prompt-dialog__error"
          role="alert"
          data-testid="nx-prompt-error"
        >
          {{ shownError }}
        </p>
        <!-- Enter in the field submits through the form; the real buttons sit in the footer. -->
        <button type="submit" class="nx-prompt-dialog__implicit" tabindex="-1" aria-hidden="true" />
      </form>
    </template>
    <template #footer>
      <div class="nx-prompt-dialog__actions" :aria-busy="props.busy || undefined">
        <NxButton
          variant="outline"
          :disabled="props.busy"
          data-testid="nx-prompt-cancel"
          @click="cancel"
        >
          {{ props.cancelLabel }}
        </NxButton>
        <NxButton :disabled="!canSubmit" data-testid="nx-prompt-submit" @click="submit">
          <span v-if="props.busy" class="nx-prompt-dialog__spinner" aria-hidden="true" />
          {{ props.confirmLabel }}
        </NxButton>
      </div>
    </template>
  </NxModal>
</template>

<style scoped>
.nx-prompt-dialog__form {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  padding-block: var(--nxt-space-2xs) var(--nxt-space-sm);
}
.nx-prompt-dialog__message {
  margin: 0 0 var(--nxt-space-2xs);
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
}
.nx-prompt-dialog__label {
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-prompt-dialog__error {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  margin: 0;
  color: var(--nxt-danger-subtle-fg);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
  animation: nx-prompt-error-in var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-prompt-dialog__error::before {
  content: '';
  flex: 0 0 auto;
  width: var(--nxt-dot-size-md);
  height: var(--nxt-dot-size-md);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-danger-bg);
}
.nx-prompt-dialog__implicit {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  border: 0;
  padding: 0;
}
.nx-prompt-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--nxt-space-xs);
  width: 100%;
}
.nx-prompt-dialog__spinner {
  display: inline-block;
  width: var(--nxt-dot-size-lg);
  height: var(--nxt-dot-size-lg);
  margin-inline-end: var(--nxt-space-2xs);
  border: calc(var(--nxt-hairline) * 2) solid currentcolor;
  border-inline-end-color: transparent;
  border-radius: var(--nxt-radius-full);
  vertical-align: middle;
  animation: nx-prompt-spin 0.8s linear infinite;
}
@keyframes nx-prompt-error-in {
  from {
    opacity: 0;
    transform: translateY(calc(var(--nxt-hairline) * -3));
  }
}
@keyframes nx-prompt-spin {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-prompt-dialog__error {
    animation: none;
  }
  .nx-prompt-dialog__spinner {
    animation-duration: 2.4s;
  }
}
</style>
